import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { ToastService } from '../shared/toast/toast.service';
import { GetSitesGQL } from '../graphql/graphql';

const ALL_USERS = gql`
  query AllUsers {
    allUsers {
      _id
      email
      name
      role
      status
      siteIds
      receiveDailyDigest
      createdAt
    }
  }
`;

const DELETE_USER = gql`
  mutation DeletePlatformUser($userId: String!) {
    deleteUser(userId: $userId) { _id }
  }
`;

const DEACTIVATE_USER = gql`
  mutation DeactivatePlatformUser($userId: String!) {
    deactivateUser(userId: $userId) { _id status }
  }
`;

const REACTIVATE_USER = gql`
  mutation ReactivatePlatformUser($userId: String!) {
    reactivateUser(userId: $userId) { _id status }
  }
`;

const INVITE_USER = gql`
  mutation InvitePlatformUser($input: InviteUserInput!) {
    inviteUser(input: $input) { _id }
  }
`;

const TOGGLE_DAILY_DIGEST = gql`
  mutation TogglePlatformDigest($userId: String!, $receive: Boolean!) {
    toggleDailyDigest(userId: $userId, receive: $receive) { _id receiveDailyDigest }
  }
`;

const UPDATE_USER_ROLE = gql`
  mutation UpdatePlatformUserRole($input: UpdateUserRoleInput!) {
    updateUserRole(input: $input) { _id role }
  }
`;

interface PlatformUser {
  _id: string;
  email: string;
  name?: string;
  role: string;
  status: string;
  siteIds: string[];
  receiveDailyDigest: boolean;
  createdAt: string;
}

@Component({
  selector: 'app-platform-users',
  templateUrl: './platform-users.component.html',
  standalone: false
})
export class PlatformUsersComponent implements OnInit {
  users: PlatformUser[] = [];
  loading = true;
  siteMap: Map<string, string> = new Map();
  sites: { _id: string; url: string }[] = [];

  showInviteModal = false;
  inviteForm: FormGroup;
  isInviting = false;
  showDeactivateModal = false;
  userToDeactivate: PlatformUser | null = null;
  showDeleteModal = false;
  userToDelete: PlatformUser | null = null;

  readonly roles = [
    { value: 'SITE_MEMBER', label: 'Member' },
    { value: 'SITE_ADMIN', label: 'Site Admin' },
    { value: 'PLATFORM_ADMIN', label: 'Platform Admin' },
  ];

  constructor(private apollo: Apollo, private toast: ToastService, private getSitesGQL: GetSitesGQL, private fb: FormBuilder) {
    this.inviteForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      role: ['SITE_MEMBER', Validators.required],
      siteIds: [[]],
    });
  }

  ngOnInit() {
    this.getSitesGQL.fetch({ fetchPolicy: 'network-only' }).subscribe((result) => {
      for (const site of result.data?.sites || []) {
        this.siteMap.set(site._id!, site.url);
        this.sites.push({ _id: site._id!, url: site.url });
      }
      this.loadUsers();
    });
  }

  siteName(id: string): string {
    return this.siteMap.get(id) || id;
  }

  loadUsers() {
    this.loading = true;
    this.apollo.query<{ allUsers: PlatformUser[] }>({ query: ALL_USERS, fetchPolicy: 'network-only' }).subscribe({
      next: (result) => {
        this.users = result.data?.allUsers || [];
        this.loading = false;
      },
      error: () => {
        this.toast.error('Failed to load users.');
        this.loading = false;
      }
    });
  }

  deactivateUser(user: PlatformUser) {
    this.userToDeactivate = user;
    this.showDeactivateModal = true;
  }

  confirmDeactivate() {
    const user = this.userToDeactivate;
    if (!user) return;
    this.showDeactivateModal = false;
    this.userToDeactivate = null;
    this.apollo.mutate({ mutation: DEACTIVATE_USER, variables: { userId: user._id } }).subscribe({
      next: () => { this.toast.success(`${user.email} deactivated.`); this.loadUsers(); },
      error: (err) => this.toast.error(err?.message || 'Could not deactivate user.'),
    });
  }

  cancelDeactivate() {
    this.showDeactivateModal = false;
    this.userToDeactivate = null;
  }

  reactivateUser(user: PlatformUser) {
    this.apollo.mutate({ mutation: REACTIVATE_USER, variables: { userId: user._id } }).subscribe({
      next: () => { this.toast.success(`${user.email} reactivated.`); this.loadUsers(); },
      error: (err) => this.toast.error(err?.message || 'Could not reactivate user.'),
    });
  }

  deleteUser(user: PlatformUser) {
    this.userToDelete = user;
    this.showDeleteModal = true;
  }

  confirmDeleteUser() {
    const user = this.userToDelete;
    if (!user) return;
    this.showDeleteModal = false;
    this.userToDelete = null;
    this.apollo.mutate({ mutation: DELETE_USER, variables: { userId: user._id } }).subscribe({
      next: () => { this.toast.success(`${user.email} deleted.`); this.loadUsers(); },
      error: (err) => this.toast.error(err?.message || 'Could not delete user.'),
    });
  }

  cancelDeleteUser() {
    this.showDeleteModal = false;
    this.userToDelete = null;
  }

  roleLabel(role: string): string {
    switch (role) {
      case 'PLATFORM_ADMIN': return 'Platform Admin';
      case 'SITE_ADMIN': return 'Site Admin';
      case 'SITE_MEMBER': return 'Member';
      default: return role;
    }
  }

  openInviteModal() {
    this.inviteForm.reset({ email: '', role: 'SITE_MEMBER', siteIds: [] });
    this.showInviteModal = true;
  }

  closeInviteModal() {
    this.showInviteModal = false;
  }

  toggleSite(siteId: string) {
    const current: string[] = this.inviteForm.get('siteIds')?.value || [];
    if (current.includes(siteId)) {
      this.inviteForm.patchValue({ siteIds: current.filter(id => id !== siteId) });
    } else {
      this.inviteForm.patchValue({ siteIds: [...current, siteId] });
    }
  }

  isSiteSelected(siteId: string): boolean {
    return (this.inviteForm.get('siteIds')?.value || []).includes(siteId);
  }

  get isPlatformAdminRole(): boolean {
    return this.inviteForm.get('role')?.value === 'PLATFORM_ADMIN';
  }

  submitInvite() {
    if (this.inviteForm.invalid || this.isInviting) return;
    this.isInviting = true;
    const { email, role, siteIds } = this.inviteForm.value;
    this.apollo.mutate({
      mutation: INVITE_USER,
      variables: { input: { email, role, siteIds: role === 'PLATFORM_ADMIN' ? [] : siteIds } },
    }).subscribe({
      next: () => {
        this.isInviting = false;
        this.showInviteModal = false;
        this.toast.success(`Invite sent to ${email}.`);
        this.loadUsers();
      },
      error: (err) => {
        this.isInviting = false;
        this.toast.error(err?.message || 'Could not send invite.');
      },
    });
  }

  toggleDailyDigest(user: PlatformUser) {
    const newValue = !user.receiveDailyDigest;
    this.apollo.mutate({ mutation: TOGGLE_DAILY_DIGEST, variables: { userId: user._id, receive: newValue } }).subscribe({
      next: () => {
        user.receiveDailyDigest = newValue;
      },
      error: (err) => this.toast.error(err?.message || 'Could not update digest setting.'),
    });
  }

  updateRole(user: PlatformUser, event: Event) {
    const role = (event.target as HTMLSelectElement).value;
    this.apollo.mutate({ mutation: UPDATE_USER_ROLE, variables: { input: { userId: user._id, role } } }).subscribe({
      next: () => {
        user.role = role;
        this.toast.success(`Role updated to ${this.roleLabel(role)}.`);
      },
      error: (err) => this.toast.error(err?.message || 'Could not update role.'),
    });
  }
}
